import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  BarChart3, Clock, Users, Activity, Download, RefreshCw,
  Filter, Calendar, TrendingUp, Layers, Target, ExternalLink, History
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import HistoryReportsTab from '../components/HistoryReportsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// History Reports is a management archive — only these logins see the tab.
const HISTORY_ALLOWED_ROLES = ['ceo', 'super_admin', 'admin', 'owner', 'account_owner'];

// The Reporting & Analytics sidebar has four links that all mount this
// one component. Without this mapping they were indistinguishable —
// "Sales Reports", "Marketing Reports" and "Activity Reports" each
// opened the identical default view. We translate the route into which
// tab (and, for History Reports, which department sub-tab) to open.
const tabForPath = (pathname) => {
  if (pathname === '/reporting/sales') return 'history-reports';
  if (pathname === '/reporting/marketing') return 'history-reports';
  if (pathname === '/reporting/activity') return 'sales-activities';
  return 'lead-response';                       // /reporting — Analytics Dashboard
};
const deptForPath = (pathname) => (pathname === '/reporting/marketing' ? 'marketing' : 'sales');

export default function ReportingDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // The History Reports tab + content render only for Admin / CEO logins.
  const canSeeHistory = HISTORY_ALLOWED_ROLES.includes((user?.role || '').toLowerCase());

  // Resolve the route to a starting tab. If the route asks for History
  // Reports but the user isn't Admin/CEO, fall back to Sales Activities
  // so the page still shows something useful instead of a blank tab.
  const resolveTab = useCallback((pathname) => {
    let t = tabForPath(pathname);
    if (t === 'history-reports' && !canSeeHistory) t = 'sales-activities';
    return t;
  }, [canSeeHistory]);

  const [activeTab, setActiveTab] = useState(() => resolveTab(location.pathname));
  const [historyDept, setHistoryDept] = useState(() => deptForPath(location.pathname));

  // Re-sync when the user clicks a different Reporting & Analytics link
  // while this component is already mounted (React Router keeps the same
  // instance across these aliased routes — only the path changes).
  useEffect(() => {
    setActiveTab(resolveTab(location.pathname));
    setHistoryDept(deptForPath(location.pathname));
  }, [location.pathname, resolveTab]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  
  // Report data states
  const [leadResponseData, setLeadResponseData] = useState([]);
  const [salesActivitiesData, setSalesActivitiesData] = useState([]);
  const [agentLifecycleData, setAgentLifecycleData] = useState({ data: [], stages: [] });
  const [sourceLifecycleData, setSourceLifecycleData] = useState({ data: [], stages: [] });
  const [campaignData, setCampaignData] = useState({ data: [], stages: [] });

  const token = sessionStorage.getItem('auth_token') || localStorage.getItem('token');

  // Navigate to contacts page with filters
  const navigateToContacts = (filters = {}) => {
    const params = new URLSearchParams();
    
    // Add date range filters
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    
    // Add specific filters
    if (filters.owner_id) params.set('owner_id', filters.owner_id);
    if (filters.owner_name) params.set('owner_name', filters.owner_name);
    if (filters.responded !== undefined) params.set('responded', filters.responded);
    if (filters.lifecycle_stage) params.set('lifecycle_stage', filters.lifecycle_stage);
    if (filters.source) params.set('source', filters.source);
    if (filters.campaign) params.set('campaign', filters.campaign);
    
    navigate(`/contacts?${params.toString()}`);
  };

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setToDate(today.toISOString().split('T')[0]);
    setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/summary?token=${token}&from_date=${fromDate}&to_date=${toDate}`
      );
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [token, fromDate, toDate]);

  const fetchLeadResponseTime = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/lead-response-time?token=${token}&from_date=${fromDate}&to_date=${toDate}`
      );
      setLeadResponseData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching lead response time:', error);
      toast.error('Failed to fetch lead response time report');
    }
  }, [token, fromDate, toDate]);

  const fetchSalesActivities = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/sales-activities?token=${token}&from_date=${fromDate}&to_date=${toDate}`
      );
      setSalesActivitiesData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sales activities:', error);
      toast.error('Failed to fetch sales activities report');
    }
  }, [token, fromDate, toDate]);

  const fetchAgentLifecycle = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/contacts-by-agent-lifecycle?token=${token}&from_date=${fromDate}&to_date=${toDate}`
      );
      setAgentLifecycleData({
        data: response.data.data || [],
        stages: response.data.lifecycle_stages || []
      });
    } catch (error) {
      console.error('Error fetching agent lifecycle:', error);
      toast.error('Failed to fetch agent lifecycle report');
    }
  }, [token, fromDate, toDate]);

  const fetchSourceLifecycle = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/contacts-by-source-lifecycle?token=${token}&from_date=${fromDate}&to_date=${toDate}`
      );
      setSourceLifecycleData({
        data: response.data.data || [],
        stages: response.data.lifecycle_stages || []
      });
    } catch (error) {
      console.error('Error fetching source lifecycle:', error);
      toast.error('Failed to fetch source lifecycle report');
    }
  }, [token, fromDate, toDate]);

  const fetchCampaignData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/contacts-by-campaign?token=${token}&from_date=${fromDate}&to_date=${toDate}`
      );
      setCampaignData({
        data: response.data.data || [],
        stages: response.data.lifecycle_stages || []
      });
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      toast.error('Failed to fetch campaign report');
    }
  }, [token, fromDate, toDate]);

  // Progressive — flip the spinner off on the first endpoint that lands
  // so a single heavy aggregation (lifecycle or campaign rollups can be
  // chunky) doesn't freeze the whole reporting view.
  const fetchAllData = useCallback(() => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    const tasks = [
      fetchSummary(),
      fetchLeadResponseTime(),
      fetchSalesActivities(),
      fetchAgentLifecycle(),
      fetchSourceLifecycle(),
      fetchCampaignData(),
    ].map((p) => Promise.resolve(p).catch((e) => {
      console.error('[ReportingDashboard] tile fetch failed:', e);
      return null;
    }));

    let firstFulfilled = false;
    const markRendered = () => {
      if (firstFulfilled) return;
      firstFulfilled = true;
      setLoading(false);
    };
    const safetyTimer = setTimeout(markRendered, 3000);
    Promise.race(tasks).then(markRendered);
    Promise.allSettled(tasks).finally(() => clearTimeout(safetyTimer));
  }, [fetchSummary, fetchLeadResponseTime, fetchSalesActivities, fetchAgentLifecycle, fetchSourceLifecycle, fetchCampaignData, fromDate, toDate]);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchAllData();
    }
  }, [fromDate, toDate, fetchAllData]);

  const handleExport = async (reportType) => {
    try {
      window.open(
        `${API_URL}/api/reports/export/${reportType}?token=${token}&from_date=${fromDate}&to_date=${toDate}`,
        '_blank'
      );
      toast.success('Export started');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const getResponseTimeColor = (formatted) => {
    if (!formatted) return 'text-slate-500';
    if (formatted.includes('minute')) return 'text-green-600 font-semibold';
    if (formatted.includes('hour')) {
      const hours = parseFloat(formatted);
      if (hours < 6) return 'text-green-600';
      if (hours < 24) return 'text-amber-600';
      return 'text-red-600';
    }
    if (formatted.includes('day')) return 'text-red-600 font-semibold';
    return 'text-slate-600';
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="reporting-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Reporting Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Advanced analytics and performance reports
          </p>
        </div>
        <Button onClick={fetchAllData} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <Label htmlFor="from-date" className="text-sm">From:</Label>
              <Input
                id="from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-40"
                data-testid="from-date-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="to-date" className="text-sm">To:</Label>
              <Input
                id="to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-40"
                data-testid="to-date-input"
              />
            </div>
            <Badge variant="outline" className="ml-auto">
              <Calendar className="h-3 w-3 mr-1" />
              {fromDate} to {toDate}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Contacts</p>
                  <p className="text-2xl font-bold">{summary.total_contacts?.toLocaleString()}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Activities</p>
                  <p className="text-2xl font-bold">{summary.total_activities?.toLocaleString()}</p>
                </div>
                <Activity className="h-8 w-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Agents</p>
                  <p className="text-2xl font-bold">{summary.total_agents}</p>
                </div>
                <Target className="h-8 w-8 text-purple-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Lead Sources</p>
                  <p className="text-2xl font-bold">{summary.total_sources}</p>
                </div>
                <Layers className="h-8 w-8 text-orange-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="lead-response" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Lead Response Time
          </TabsTrigger>
          <TabsTrigger value="sales-activities" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Sales Activities
          </TabsTrigger>
          <TabsTrigger value="agent-lifecycle" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Agent × Lifecycle
          </TabsTrigger>
          <TabsTrigger value="source-lifecycle" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Source × Lifecycle
          </TabsTrigger>
          <TabsTrigger value="campaign-drilldown" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Campaign Drill-down
          </TabsTrigger>
          {canSeeHistory && (
            <TabsTrigger value="history-reports" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              History Reports
            </TabsTrigger>
          )}
        </TabsList>

        {/* Report 1: Lead Response Time */}
        <TabsContent value="lead-response">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Lead Response Time</CardTitle>
                <CardDescription>Average time to first contact by agent (only counts real engagement activities: calls, notes, emails, WhatsApp)</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExport('lead-response-time')}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact Owner</TableHead>
                    <TableHead className="text-right">Contacts</TableHead>
                    <TableHead className="text-right">Responded</TableHead>
                    <TableHead className="text-right">Avg Response Time</TableHead>
                    <TableHead className="text-right">Min</TableHead>
                    <TableHead className="text-right">Max</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leadResponseData.map((row, idx) => (
                    <TableRow key={idx} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{row.owner_name || 'Unassigned'}</TableCell>
                      <TableCell 
                        className="text-right cursor-pointer hover:text-blue-600 hover:underline"
                        onClick={() => navigateToContacts({ 
                          owner_id: row.owner_id,
                          owner_name: row.owner_name 
                        })}
                        title="Click to view contacts"
                      >
                        <span className="flex items-center justify-end gap-1">
                          {row.contact_count}
                          <ExternalLink className="h-3 w-3 opacity-50" />
                        </span>
                      </TableCell>
                      <TableCell 
                        className="text-right cursor-pointer hover:text-green-600 hover:underline"
                        onClick={() => navigateToContacts({ 
                          owner_id: row.owner_id,
                          owner_name: row.owner_name,
                          responded: 'true'
                        })}
                        title="Click to view responded contacts"
                      >
                        <span className="flex items-center justify-end gap-1">
                          {row.responded_count}
                          <ExternalLink className="h-3 w-3 opacity-50" />
                        </span>
                      </TableCell>
                      <TableCell className={`text-right ${getResponseTimeColor(row.avg_response_time_formatted)}`}>
                        {row.avg_response_time_formatted || 'N/A'}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground text-sm">
                        {row.min_response_time_formatted || '-'}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground text-sm">
                        {row.max_response_time_formatted || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {leadResponseData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No data available for selected date range
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 2: Sales Activities */}
        <TabsContent value="sales-activities">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Sales Activities by Agent</CardTitle>
                <CardDescription>Number of activities per agent</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExport('sales-activities')}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent Name</TableHead>
                    <TableHead className="text-right">Total Activities</TableHead>
                    <TableHead className="text-right">Contacts Worked</TableHead>
                    <TableHead className="text-right">Avg Activities/Contact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesActivitiesData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{row.agent_name || 'Unknown'}</TableCell>
                      <TableCell className="text-right font-semibold">{row.total_activities}</TableCell>
                      <TableCell className="text-right">{row.contacts_worked}</TableCell>
                      <TableCell className="text-right">{row.avg_activities_per_contact}</TableCell>
                    </TableRow>
                  ))}
                  {salesActivitiesData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No data available for selected date range
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 3: Agent × Lifecycle Matrix */}
        <TabsContent value="agent-lifecycle">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Contacts by Agent & Lifecycle Stage</CardTitle>
                <CardDescription>Distribution of contacts across lifecycle stages per agent</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExport('contacts-by-agent-lifecycle')}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white">Agent</TableHead>
                    {agentLifecycleData.stages.slice(0, 10).map(stage => (
                      <TableHead key={stage} className="text-center text-xs px-2">
                        {stage?.substring(0, 12) || 'Unknown'}
                      </TableHead>
                    ))}
                    <TableHead className="text-right font-bold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentLifecycleData.data.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="sticky left-0 bg-white font-medium">
                        {row.agent_name || 'Unassigned'}
                      </TableCell>
                      {agentLifecycleData.stages.slice(0, 10).map(stage => (
                        <TableCell key={stage} className="text-center">
                          {row[stage] || 0}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold">{row.total}</TableCell>
                    </TableRow>
                  ))}
                  {agentLifecycleData.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={agentLifecycleData.stages.length + 2} className="text-center text-muted-foreground py-8">
                        No data available for selected date range
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 4: Source × Lifecycle Matrix */}
        <TabsContent value="source-lifecycle">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Contacts by Source & Lifecycle Stage</CardTitle>
                <CardDescription>Distribution of contacts across lifecycle stages per source</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExport('contacts-by-source-lifecycle')}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white">Source</TableHead>
                    {sourceLifecycleData.stages.slice(0, 10).map(stage => (
                      <TableHead key={stage} className="text-center text-xs px-2">
                        {stage?.substring(0, 12) || 'Unknown'}
                      </TableHead>
                    ))}
                    <TableHead className="text-right font-bold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sourceLifecycleData.data.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="sticky left-0 bg-white font-medium max-w-[200px] truncate" title={row.source}>
                        {row.source || 'Unknown'}
                      </TableCell>
                      {sourceLifecycleData.stages.slice(0, 10).map(stage => (
                        <TableCell key={stage} className="text-center">
                          {row[stage] || 0}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold">{row.total}</TableCell>
                    </TableRow>
                  ))}
                  {sourceLifecycleData.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={sourceLifecycleData.stages.length + 2} className="text-center text-muted-foreground py-8">
                        No data available for selected date range
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 5: Campaign Drill-down */}
        <TabsContent value="campaign-drilldown">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Campaign Drill-down</CardTitle>
                <CardDescription>Detailed breakdown by source campaigns and lifecycle stages</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleExport('contacts-by-campaign')}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {campaignData.data.map((group, groupIdx) => (
                <div key={groupIdx} className="mb-6">
                  <div className="flex items-center gap-2 mb-2 p-2 bg-slate-100 rounded">
                    <Badge variant="secondary" className="font-semibold">
                      {group.category}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Total: {group.total} contacts
                    </span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Campaign</TableHead>
                        {campaignData.stages.slice(0, 8).map(stage => (
                          <TableHead key={stage} className="text-center text-xs px-2">
                            {stage?.substring(0, 10) || '?'}
                          </TableHead>
                        ))}
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.campaigns.slice(0, 10).map((campaign, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium max-w-[200px] truncate" title={campaign.campaign}>
                            {campaign.campaign || 'Unknown'}
                          </TableCell>
                          {campaignData.stages.slice(0, 8).map(stage => (
                            <TableCell key={stage} className="text-center">
                              {campaign[stage] || 0}
                            </TableCell>
                          ))}
                          <TableCell className="text-right font-semibold">{campaign.total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
              {campaignData.data.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  No data available for selected date range
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 6: History Reports — Admin / CEO only. The trigger above
            is also gated, and the component's API is gated server-side,
            so this is defence-in-depth, not the sole guard. */}
        {canSeeHistory && (
          <TabsContent value="history-reports">
            {/* key forces a fresh mount when the route switches the
                department (Sales Reports ↔ Marketing Reports), so the
                sub-tab opens on the side the menu item promised. */}
            <HistoryReportsTab key={historyDept} initialDepartment={historyDept} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
